import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme configurations
export const themes = {
  whimsical: {
    id: 'whimsical',
    name: 'Explorer',
    description: 'Warm vintage explorer theme',
    colors: {
      primary: '#2D6A4F', // forest green
      secondary: '#B8860B', // dark goldenrod / brass
      accent: '#C44536', // compass rose red
      background: '#F9F6F0', // aged paper
      text: '#2C1810', // aged ink
      textSecondary: '#5C4033', // warm brown
    },
    header: {
      bg: 'bg-gradient-to-r from-emerald-800 via-emerald-700 to-emerald-800',
      text: 'text-white',
      border: 'border-emerald-900',
      buttonPrimary: 'bg-amber-100 hover:bg-amber-200 text-emerald-900 font-semibold shadow-md',
      buttonSecondary: 'text-amber-100 hover:text-amber-200 border-amber-200/50'
    },
    footer: {
      bg: 'bg-gradient-to-br from-stone-900 to-stone-800',
      text: 'text-stone-400',
      heading: 'text-amber-100',
      link: 'text-stone-400 hover:text-amber-200',
      border: 'border-stone-700'
    },
    buttons: {
      primary: 'bg-gradient-to-r from-emerald-700 to-emerald-800 hover:from-emerald-800 hover:to-emerald-900 text-white shadow-lg hover:shadow-xl',
      secondary: 'border-2 border-emerald-700 text-emerald-800 hover:bg-emerald-50 bg-white',
      accent: 'bg-amber-600 hover:bg-amber-700 text-white'
    },
    cards: {
      bg: 'bg-white',
      border: 'border-stone-200',
      shadow: 'shadow-lg hover:shadow-xl',
      heading: 'text-stone-900',
      text: 'text-stone-600'
    },
    hero: {
      background: 'bg-gradient-to-br from-stone-50 via-amber-50/30 to-emerald-50/20',
      badge: {
        bg: 'bg-amber-50 backdrop-blur-sm',
        text: 'text-amber-800',
        border: 'border border-amber-200',
        label: 'Track. Share. Inspire.'
      },
      title: {
        main: 'text-stone-900',
        gradient: 'bg-gradient-to-r from-emerald-800 via-emerald-700 to-amber-700 bg-clip-text text-transparent'
      },
      cards: {
        left: {
          bg: 'bg-gradient-to-br from-emerald-50 to-emerald-100',
          border: 'border-2 border-emerald-200',
          iconBg: 'bg-emerald-700'
        },
        center: {
          bg: 'bg-white/95 backdrop-blur-sm',
          border: 'border border-stone-200',
          text: 'text-stone-800',
          highlight1: 'text-emerald-700',
          highlight2: 'text-amber-700',
          tagline: 'text-emerald-700'
        },
        right: {
          bg: 'bg-gradient-to-br from-amber-50 to-amber-100',
          border: 'border-2 border-amber-200',
          iconBg: 'bg-amber-600'
        }
      },
      buttons: {
        primary: 'bg-gradient-to-r from-emerald-700 to-emerald-800 hover:from-emerald-800 hover:to-emerald-900 text-white',
        secondary: 'border-2 border-emerald-700 text-emerald-800 hover:bg-emerald-50'
      },
      decorativeElements: 'whimsical'
    }
  },
  vintage: {
    id: 'vintage',
    name: 'Vintage',
    description: 'Vintage nautical explorer theme',
    colors: {
      primary: '#8B4513', // saddle brown
      secondary: '#654321', // dark brown
      accent: '#A0522D', // sienna
      background: '#f5ebd2', // cream
      text: '#2c1810', // dark brown
      textSecondary: '#3d2817', // medium brown
    },
    header: {
      bg: 'bg-[#f4e4c1]',
      text: 'text-[#2c1810]',
      border: 'border-[#8B4513]',
      buttonPrimary: 'bg-gradient-to-r from-[#654321] to-[#8B4513] hover:from-[#8B4513] hover:to-[#A0522D] text-[#f4e4c1]',
      buttonSecondary: 'text-[#654321] hover:text-[#8B4513]',
      fontFamily: 'Georgia, serif'
    },
    footer: {
      bg: 'bg-gradient-to-br from-[#3d2817] to-[#2c1810]',
      text: 'text-[#d4a574]',
      heading: 'text-[#f4e4c1]',
      link: 'text-[#c9a86a] hover:text-[#f4e4c1]',
      border: 'border-[#654321]',
      fontFamily: 'Georgia, serif'
    },
    buttons: {
      primary: 'bg-gradient-to-r from-[#654321] to-[#8B4513] hover:from-[#8B4513] hover:to-[#A0522D] text-[#f4e4c1] shadow-lg hover:shadow-xl border-2 border-[#3d2817]',
      secondary: 'border-3 border-[#654321] text-[#2c1810] hover:bg-[#f5ebd2] bg-[#f4e4c1]',
      accent: 'bg-[#A0522D] hover:bg-[#8B4513] text-[#f4e4c1]',
      fontFamily: 'Georgia, serif'
    },
    cards: {
      bg: 'bg-gradient-to-br from-[#f5ebd2] to-[#f4e4c1]',
      border: 'border-[#8B4513] border-3',
      shadow: 'shadow-lg hover:shadow-xl',
      heading: 'text-[#2c1810]',
      text: 'text-[#3d2817]',
      fontFamily: 'Georgia, serif'
    },
    hero: {
      background: 'linear-gradient(135deg, #d4b896 0%, #c9a86a 25%, #b8956a 50%, #a67c52 75%, #8b6f47 100%)',
      hasVintageOverlays: true,
      badge: {
        style: {
          background: 'linear-gradient(135deg, rgba(139, 69, 19, 0.9), rgba(160, 82, 45, 0.9))',
          color: '#f4e4c1',
          border: '2px solid #8B4513',
          fontFamily: 'Georgia, serif'
        },
        label: 'Chart Your Journey - Explore Your World'
      },
      title: {
        style: {
          color: '#2c1810',
          textShadow: '2px 2px 4px rgba(0,0,0,0.2)',
          fontFamily: 'Georgia, serif'
        },
        gradientStyle: {
          background: 'linear-gradient(135deg, #654321, #8B4513, #A0522D)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }
      },
      cards: {
        commonStyle: {
          background: 'linear-gradient(135deg, rgba(244, 228, 193, 0.95), rgba(218, 196, 153, 0.95))',
          border: '3px solid #8B4513',
          boxShadow: '0 4px 6px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)'
        },
        iconStyle: {
          background: 'linear-gradient(135deg, #654321, #8B4513)',
          boxShadow: '0 2px 4px rgba(0,0,0,0.3)'
        },
        textStyle: {
          title: { color: '#2c1810', fontFamily: 'Georgia, serif' },
          body: { color: '#3d2817', fontFamily: 'Georgia, serif' }
        },
        centerStyle: {
          background: 'linear-gradient(135deg, rgba(255, 248, 220, 0.98), rgba(245, 235, 210, 0.98))',
          border: '4px solid #8B4513',
          boxShadow: '0 8px 16px rgba(0,0,0,0.3), inset 0 2px 4px rgba(255,255,255,0.5)',
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h60v60H0z' fill='%23f5ebd2'/%3E%3Cpath d='M30 30m-2 0a2 2 0 1 0 4 0a2 2 0 1 0 -4 0' fill='%23d4a574' opacity='0.1'/%3E%3C/svg%3E")`
        }
      },
      buttons: {
        primaryStyle: {
          background: 'linear-gradient(135deg, #654321, #8B4513, #A0522D)',
          color: '#f4e4c1',
          border: '3px solid #3d2817',
          fontFamily: 'Georgia, serif',
          fontWeight: 'bold',
          textShadow: '1px 1px 2px rgba(0,0,0,0.3)'
        },
        secondaryStyle: {
          border: '3px solid #654321',
          color: '#2c1810',
          background: 'rgba(244, 228, 193, 0.8)',
          fontFamily: 'Georgia, serif',
          fontWeight: 'bold'
        }
      },
      decorativeElements: 'nautical'
    }
  }
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState(() => {
    // Load theme from localStorage or default to whimsical
    const savedTheme = localStorage.getItem('trackme-theme');
    return savedTheme || 'whimsical';
  });

  useEffect(() => {
    // Save theme to localStorage whenever it changes
    localStorage.setItem('trackme-theme', currentTheme);
  }, [currentTheme]);

  const theme = themes[currentTheme];

  const switchTheme = (themeId) => {
    if (themes[themeId]) {
      setCurrentTheme(themeId);
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, currentTheme, switchTheme, themes }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
